import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ClockIcon, DangerIcon, FamilyIcon, HealthAiIcon, LocationIcon, ModelIcon, UsersIcon, VideoIcon, WatchModelsIcon } from "../icons/icons";

// ─── Floating Particle ───────────────────────────────────────────────────────
const FloatingParticle: React.FC<{ x: number; delay: number; color?: string }> = ({ x, delay, color = "#FF6701" }) => (
    <motion.div
        className="absolute w-1 h-1 rounded-full pointer-events-none"
        style={{ left: `${x}%`, bottom: "10%", backgroundColor: color, opacity: 0.6 }}
        animate={{ y: [0, -45, 0], opacity: [0, 0.7, 0], scale: [0, 1.2, 0] }}
        transition={{ duration: 2, delay, repeat: Infinity, ease: "easeInOut" }}
    />
);

// ─── Feature Chip Component ──────────────────────────────────────────────────
interface FeatureChipProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    index: number;
}

const FeatureChip: React.FC<FeatureChipProps> = ({ icon, title, description, index }) => {
    const [hovered, setHovered] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const spawnParticles = () => {
        const ps = Array.from({ length: 3 }, (_, i) => ({ id: Date.now() + i, x: 20 + Math.random() * 60 }));
        setParticles(ps);
        timerRef.current = setTimeout(() => setParticles([]), 2100);
    };

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    return (
        <motion.div
            className="relative flex items-center gap-3 cursor-pointer overflow-hidden rounded-2xl p-3"
            style={{
                background: hovered ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,247,237,0.85) 100%)" : "transparent",
                border: `1px solid ${hovered ? "rgba(255,103,1,0.25)" : "transparent"}`,
                backdropFilter: hovered ? "blur(12px)" : "none",
                WebkitBackdropFilter: hovered ? "blur(12px)" : "none",
                boxShadow: hovered ? "0 8px 24px -6px rgba(255,103,1,0.18)" : "none",
                transition: "all 0.3s ease",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
            whileHover={{ scale: 1.04, y: -4 }}
            whileTap={{ scale: 0.97 }}
            onHoverStart={() => {
                setHovered(true);
                spawnParticles();
            }}
            onHoverEnd={() => setHovered(false)}
        >
            {/* Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {particles.map((p) => (
                    <FloatingParticle key={p.id} x={p.x} delay={0} color="#FF6701" />
                ))}
            </div>

            {/* Top shimmer divider */}
            <motion.div
                className="absolute top-0 left-4 right-4 h-[1.5px] rounded-full"
                style={{ background: "linear-gradient(90deg, transparent, #FF6701, transparent)" }}
                animate={{ opacity: hovered ? 0.7 : 0, scaleX: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />

            {/* Icon */}
            <motion.div
                className="flex-shrink-0 relative text-[#FF6701]"
                animate={{ rotate: hovered ? [0, -8, 8, 0] : 0, scale: hovered ? 1.1 : 1 }}
                transition={{ duration: 0.4 }}
            >
                {hovered && (
                    <motion.div
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{ border: "1.5px solid rgba(255,103,1,0.35)" }}
                        initial={{ scale: 1, opacity: 0.5 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 0.9, repeat: Infinity }}
                    />
                )}
                {icon}
            </motion.div>

            {/* Text */}
            <div className="flex flex-col">
                <motion.h3
                    className="font-semibold mb-0.5 text-sm transition-colors duration-300"
                    animate={{ color: hovered ? "#FF6701" : "#1e293b" }}
                >
                    {title}
                </motion.h3>
                <motion.p
                    className="text-xs leading-relaxed transition-colors duration-300"
                    animate={{ color: hovered ? "#78716c" : "#5D697F" }}
                >
                    {description}
                </motion.p>
            </div>
        </motion.div>
    );
};

interface HeroSectionProps {
    onCompareClick: () => void;
}

const WatchHeroSection: React.FC<HeroSectionProps> = ({ onCompareClick }) => {
    const { t, i18n } = useTranslation("WatchHero");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [selectedWatch, setSelectedWatch] = useState(0);

    const watches = [
        {
            id: 0,
            color: "yellow",
            image: "../../../media/watch4.webp",
            bgColor: "../../../media/watchbackground4.webp",
            name: t("watches.0.name"),
            tagline: t("watches.0.tagline"),
            description: t("watches.0.description"),
        },
        {
            id: 1,
            color: "orange",
            image: "../../../media/watch2.webp",
            bgColor: "../../../media/watchbackground2.webp",
            name: t("watches.1.name"),
            tagline: t("watches.1.tagline"),
            description: t("watches.1.description"),
        },
        {
            id: 2,
            color: "blue",
            image: "../../../media/watch1.webp",
            bgColor: "../../../media/watchbackground1.webp",
            name: t("watches.2.name"),
            tagline: t("watches.2.tagline"),
            description: t("watches.2.description"),
        },
        {
            id: 3,
            color: "pink",
            image: "../../../media/watch3.webp",
            bgColor: "../../../media/watchbackground3.webp",
            name: t("watches.3.name"),
            tagline: t("watches.3.tagline"),
            description: t("watches.3.description"),
        },
    ];

    const features = [
        {
            title: t("features.liveLocation.title"),
            description: t("features.liveLocation.description"),
            icon: <LocationIcon />,
        },
        {
            title: t("features.familySharing.title"),
            description: t("features.familySharing.description"),
            icon: <FamilyIcon />,
        },
        {
            title: t("features.sosMode.title"),
            description: t("features.sosMode.description"),
            icon: <DangerIcon />,
        },
    ];

    const handleSmoothScroll = () => {
        const targetElement = document.getElementById("comparison-section") || document.querySelector('[data-section="comparison"]') || document.querySelector(".comparison-section");

        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });
        } else {
            window.scrollTo({
                top: window.innerHeight,
                behavior: "smooth",
            });
        }

        onCompareClick();
    };

    const buttons = [
        {
            text: t("buttons.compare"),
            icon: <ModelIcon />,
            className: "bg-[#FF6701] text-white",
            shadow: "0 10px 25px -5px rgba(249, 115, 22, 0.4)",
            onClick: handleSmoothScroll,
        },
        {
            text: t("buttons.recommended"),
            icon: <VideoIcon />,
            className: "bg-white text-[#293048]",
            shadow: "0 10px 25px -5px rgba(107, 114, 128, 0.2)",
            onClick: () => {},
        },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative pt-0 sm:pt-16 overflow-x-hidden"
            dir={isRTL ? "rtl" : "ltr"}
        >
            {/* Hero Section */}
            <div className="transition-all duration-500">
                <div className="container mx-auto px-4 sm:px-10 pb-4 sm:pb-8 pt-4 sm:pt-20">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6 sm:gap-12">
                        {/* Text Content */}
                        <div className="md:w-1/2 relative text-center sm:text-start">
                            <div className="border border-[#222323] rounded-full w-fit py-1 px-3 mb-3 sm:mb-8 mx-auto sm:mx-0">
                                <p className="text-[#293048] font-medium text-xs sm:text-sm">{t("badge")}</p>
                            </div>

                            <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4 text-black">{t("title")}</h1>
                            <h2 className="text-xl sm:text-4xl mb-2 sm:mb-4 font-medium">{t("subtitle.part1")}</h2>
                            <span className="text-xl sm:text-4xl font-medium bg-gradient-to-r from-[#A44302] to-[#F86300] bg-clip-text text-transparent">{t("subtitle.highlight")}</span>
                            <p className="text-[#293048] my-4 sm:my-8 text-sm sm:text-base leading-relaxed">{t("description")}</p>

                            <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-3 sm:gap-4">
                                {buttons.map((btn, idx) => (
                                    <motion.button
                                        key={idx}
                                        onClick={btn.onClick}
                                        whileHover={{
                                            scale: 1.03,
                                            boxShadow: btn.shadow,
                                        }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`w-full sm:w-fit text-sm sm:text-base flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 ${btn.className}`}
                                    >
                                        {btn.icon}
                                        <span>{btn.text}</span>
                                    </motion.button>
                                ))}
                            </div>
                        </div>

                        {/* Watch Display */}
                        <div className="md:w-1/2 relative flex justify-center items-center">
                            <div className="relative inline-block w-full">
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={selectedWatch}
                                        src={watches[selectedWatch].bgColor}
                                        alt={t("images.watchBackground")}
                                        className="w-full h-auto"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.4 }}
                                    />
                                </AnimatePresence>

                                <div className="absolute inset-0 z-10 flex flex-col justify-between p-4">
                                    <div className="flex justify-start items-center">
                                        <div
                                            className="bg-white px-4 py-2 flex items-center gap-2 text-sm border border-[#EAEDF2]"
                                            style={{
                                                borderStartStartRadius: "110px",
                                                borderEndStartRadius: "110px",
                                                borderStartEndRadius: "52px",
                                                borderEndEndRadius: "52px",
                                            }}
                                        >
                                            <WatchModelsIcon />
                                            <div className="flex flex-col text-[#070D27]">
                                                <span className="font-bold">{watches[selectedWatch].tagline}</span>
                                                <span className="text-xs">{watches[selectedWatch].description}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-full w-fit h-fit flex gap-2 p-3 z-20">
                                        {watches.map((watch) => (
                                            <button
                                                key={watch.id}
                                                onClick={() => setSelectedWatch(watch.id)}
                                                className="w-fit h-fit rounded-full overflow-hidden transition-all cursor-pointer origin-bottom"
                                                style={{
                                                    transform: selectedWatch === watch.id ? "scale(1.35)" : "scale(1)",
                                                }}
                                            >
                                                <img src={watch.image} alt={watch.name} className="w-12 h-12 object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="container mx-auto px-4 sm:px-10 sm:pt-10 pt-4">
                <motion.div
                    className="flex flex-col md:flex-row-reverse justify-between sm:items-center gap-6 sm:gap-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                >
                    {/* Feature Items */}
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full justify-around">
                        {features.map((feature, index) => (
                            <FeatureChip
                                key={index}
                                index={index}
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.description}
                            />
                        ))}
                    </div>

                    {/* Partnership Badge */}
                    <motion.div
                        className="bg-orange-50/60 py-3.5 px-3 lg:px-6 rounded-2xl w-full md:w-auto border border-orange-100/60 cursor-pointer overflow-hidden"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1.7 }}
                        whileHover={{
                            scale: 1.03,
                            boxShadow: "0 8px 24px -6px rgba(255,103,1,0.18)",
                            borderColor: "rgba(255,103,1,0.3)",
                        }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <div className="flex items-center justify-center gap-3">
                            <img src="../../../media/HamrahAvval.svg" alt={t("partnership.logoAlt")} className="h-4" />
                            <p className="text-[#5D697F] text-sm border-s border-gray-300 ps-3">{t("partnership.text")}</p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default WatchHeroSection;
